import React, { useRef, useCallback } from 'react'
import { Box, Stack, Typography, Skeleton, styled } from '@mui/material'
import { MessageSender, RawVoiceMessage } from '@weblite/ts-action/message'
import { playAudio, pauseAudio, useAudioState } from '@weblite/audio'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { ActionHandler, ActionHandlerState } from '$common/action-handler'
import { useSeek, useContentFileStatus } from '$common/react-hooks/message'
import { getClientUserId } from '$logic/client-user'
import { formatDuration } from '$func/time'
import { t } from '$i18n'
import * as audioPlayerService from '$logic/audio-player'
import { useSenderName } from '$logic/message/react-hook'

const Container = styled(Stack)`
  flex-grow: 1;
  position: relative;
  flex-direction: row;
  align-items: center;

  padding: 10px 0;
  box-sizing: border-box;
  overflow: hidden;
`

const ContentContainer = styled(Box)`
  flex-grow: 1;
  margin-right: 10px;
  overflow: hidden;
`

const VoiceMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

const playStateToIconMap = {
  NOT_PLAYING: PlayIcon,
  PLAYING: PauseIcon,
  PAUSED: PlayIcon,
}

export function Voice({
  attachment,
  sender,
  selected,
  selectOnTap,
  roomId,
  iid,
}: {
  attachment: RawVoiceMessage['attachment']
  sender: MessageSender
  selected: boolean
  selectOnTap: boolean
  iid: number
  roomId: string
}) {
  const { duration, fileId } = attachment

  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId: null, messageType: 'VOICE' })

  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const canSeek = state === 'AVAILABLE'
  const seekValue = useSeek({
    src: blobURL,
    canSeek,
    containerRef,
    seekContainerRef,
  })

  const audioDurationText = formatDuration(duration)
  const spentDurationText = formatDuration(duration * seekValue) // in case of playing

  const CustomActionIcon =
    state === 'AVAILABLE' ? playStateToIconMap[playState] : null

  const { name: senderName, loading: senderNameLoading } = useSenderName(sender)
  const fromMe = sender.id === getClientUserId()

  const togglePlay = useCallback(() => {
    if (playState === 'PLAYING') pauseAudio(blobURL)
    else {
      audioPlayerService.setData(roomId, iid)
      playAudio(blobURL, duration / 1000, { name: senderName, type: 'VOICE' })
    }
  }, [playState, blobURL, duration, senderName])

  return (
    <Container ref={containerRef}>
      <ActionHandler
        state={state as ActionHandlerState}
        percentage={percentage}
        onCancelClick={cancelOperation}
        onDownloadClick={startDownload}
        onCustomActionIconClick={togglePlay}
        customActionIconComp={CustomActionIcon && <CustomActionIcon />}
        selected={selected}
        selectOnTap={selectOnTap}
      />

      <ContentContainer>
        {!fromMe && senderNameLoading ? (
          <Skeleton
            variant="text"
            width="50px"
            height="16px"
            sx={{ marginBottom: '8px' }}
          />
        ) : (
          <Typography
            color="semiBlack"
            variant="caption1"
            sx={{ display: 'block', marginBottom: '8px', lineHeight: '16px' }}
            noWrap
          >
            {fromMe ? t('common.you') : senderName}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" sx={{ overflow: 'hidden' }}>
          <VoiceMetaTypography>{audioDurationText}</VoiceMetaTypography>

          {(playState === 'PLAYING' || playState === 'PAUSED') && (
            <>
              <VoiceMetaTypography
                sx={{
                  color: '#c4c4c4',
                  margin: '0 4px',
                }}
              >
                /
              </VoiceMetaTypography>
              <VoiceMetaTypography>{spentDurationText}</VoiceMetaTypography>
            </>
          )}
        </Stack>
      </ContentContainer>
    </Container>
  )
}
